
'use client';

import Link from 'next/link';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from './ui/separator';
import { useAuth } from '@/context/AuthContext';
import Image from 'next/image';
import { ScrollArea } from './ui/scroll-area';
import { useGameReviews } from '@/context/GameReviewContext';
import { Skeleton } from './ui/skeleton';
import { useCategory } from '@/context/CategoryContext';
import { Button } from './ui/button';
import { Home, FileText, Store, PlaySquare, PlusCircle } from 'lucide-react';
import { AddGameReviewDialog } from './add-game-review-dialog';
import { AddCategoryDialog } from './add-category-dialog';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';


export function LeftSidebar() {
  const { user, isOwner } = useAuth();
  const { reviews: gamesPlayed, loading: reviewsLoading } = useGameReviews();
  const { categories, loading: categoriesLoading } = useCategory();
  const pathname = usePathname();

  const navLinks = [
    { href: '/', icon: Home, label: 'Home' },
    { href: '/cv', icon: FileText, label: 'CV' },
    { href: '/marketplace', icon: Store, label: 'Marketplace' },
    { href: '/gaming', icon: PlaySquare, label: 'Gaming' },
  ];

  return (
    <div className="p-4 space-y-4 h-full flex flex-col">
      <nav>
        <ul className="space-y-2">
          {user && (
            <li>
              <Link href={`/friends/${user.uid}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.photoURL || 'https://placehold.co/40x40.png'} alt={user.displayName || "User"} data-ai-hint="person portrait" />
                  <AvatarFallback>{user.displayName?.split(' ').map(n=>n[0]).join('') || 'U'}</AvatarFallback>
                </Avatar>
                <span className="font-semibold">{user.displayName}</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>
      <Separator />

      <div className="lg:hidden">
        <nav>
          <ul className="space-y-1">
            {navLinks.map(({ href, icon: Icon, label }) => (
              <li key={href}>
                 <Link href={href} className={cn(
                    'flex items-center gap-3 p-2 rounded-lg transition-colors',
                     pathname === href ? 'bg-secondary text-primary font-semibold' : 'hover:bg-secondary'
                 )}>
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                 </Link>
              </li>
            ))}
          </ul>
        </nav>
        <Separator className="my-4" />
      </div>

      <div className="flex justify-between items-center px-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Categories
        </h3>
        {isOwner && (
          <AddCategoryDialog>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <PlusCircle className="h-4 w-4" />
            </Button>
          </AddCategoryDialog>
        )}
      </div>
      <nav className="flex-shrink-0">
        <ul className="space-y-2">
          {categoriesLoading ? (
             Array.from({ length: 8 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 px-2 py-1">
                    <Skeleton className="h-6 w-6 rounded-md" />
                    <Skeleton className="h-4 w-2/3" />
                </li>
            ))
          ) : (
            categories.map((item) => (
              <li key={item.id}>
                <Link href={`/category/${item.slug}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors">
                  <span className="font-medium">{item.name}</span>
                </Link>
              </li>
            ))
          )}
        </ul>
      </nav>
      
      <Separator />

      <div className="flex-1 min-h-0">
        <div className="flex justify-between items-center px-2 mb-2">
            <h3 className="text-sm font-semibold text-muted-foreground">
            Games Played
            </h3>
            {isOwner && (
              <AddGameReviewDialog>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </AddGameReviewDialog>
            )}
        </div>
        <ScrollArea className="h-full">
            <ul className="space-y-0">
            {reviewsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                    <li key={i} className="flex items-center gap-3 px-2 py-1">
                        <Skeleton className="h-6 w-6 rounded-md" />
                        <Skeleton className="h-4 w-2/3" />
                    </li>
                ))
            ) : (
                gamesPlayed.map((game) => (
                    <li key={game.id}>
                    <Link href={`/game/${game.id}`} className="flex items-center gap-3 px-2 py-1 rounded-lg hover:bg-secondary transition-colors">
                        {game.iconUrl ? (
                            <Image 
                                src={game.iconUrl} 
                                alt={`${game.name} icon`} 
                                width={24} 
                                height={24} 
                                className="rounded-md object-cover"
                            />
                        ) : (
                           <Skeleton className="h-6 w-6 rounded-md" />
                        )}
                        <span className="font-medium text-xs">{game.name}</span>
                    </Link>
                    </li>
                ))
            )}
            </ul>
        </ScrollArea>
      </div>
    </div>
  );
}
